import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { createTemplate, updateTemplate, getTemplate, testTemplateDraft } from "../../../services/TemplatesServices";
import { generateAIResponse } from "../../../services/AIServices";
import UserContext from "../../../contexts/UserContext";
import { 
  IconArrowLeft, 
  IconDeviceFloppy, 
  IconMail, 
  IconEye, 
  IconSend,
  IconRobot,
  IconCheck,
  IconX
} from "@tabler/icons-react";
import styled from "styled-components";
import NotificationsContext from "../../../contexts/NotificationsContext";

const EmailTemplateForm = ({ agentId: propAgentId = null }) => {
  const { user } = useContext(UserContext);
  const { notify,setNotify } = useContext(NotificationsContext);
  const navigate = useNavigate();
  const { id, agentId: routeAgentId } = useParams();
  const isEditing = !!id;
  
  // Usar agentId da rota se disponível, senão usar prop
  const agentId = routeAgentId || propAgentId;
  
  // Detecta se está no contexto do AgentHub
  const isInsideAgentHub = Boolean(routeAgentId) || window.location.pathname.includes('/agent-hub/');
  
  // Função para navegar de volta
  const handleBackNavigation = () => {
    if (isInsideAgentHub && agentId) {
      navigate(`/agents/${agentId}/hub`);
    } else {
      navigate("/templates");
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("confirmação de agendamento para consultório médico");
  const [isGenerating, setIsGenerating] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  // Shortcodes disponíveis
  const availableShortcodes = [
    { code: "{{nome_cliente}}", description: "Nome do cliente" },
    { code: "{{telefone}}", description: "Telefone do cliente" },
    { code: "{{data}}", description: "Data do agendamento/coleta de dados" },
    { code: "{{hora}}", description: "Hora do agendamento/coleta de dados" },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    defaultValues: {
      name: "",
      action: "email",
      template_type: "email",
      email_display_name: "",
      email_subject: "",
      email_html_content: "",
      email_from: ""
    }
  });

  const watchedValues = watch();

  // Carrega dados do template para edição
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      getTemplate(id).then((data) => {
        setValue("name", data.name);
        setValue("action", data.action || "email");
        setValue("template_type", "email");
        setValue("email_display_name", data.email_display_name || "");
        setValue("email_subject", data.email_subject || "");
        setValue("email_html_content", data.email_html_content || "");
        setValue("email_from", data.email_from || "");
        setIsLoading(false);
      }).catch((error) => {
        console.error('Erro ao carregar template:', error);
        
        // Tratamento específico para erro da API com key 'error'
        let errorMessage = "Erro ao carregar template.";
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setNotify({
          title: "Erro!",
          message: errorMessage,
          type: "error",
          visible: true,
        });
        
        setIsLoading(false);
        handleBackNavigation();
      });
    }
  }, [isEditing, id, setValue, navigate, setNotify]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const templateData = {
        name: data.name,
        action: data.action,
        template_type: "email",
        email_display_name: data.email_display_name,
        email_subject: data.email_subject,
        email_html_content: data.email_html_content,
        email_from: data.email_from,
        user: user.user_id,
        account_id: user.account_id,
        // Fallback text message
        message: {
          text: data.email_subject
        }
      };

      // Adicionar agent_id se fornecido (contexto do AgentHub)
      if (agentId) {
        templateData.agent = agentId;
      }

      if (isEditing) {
        await updateTemplate(id, templateData);
      } else {
        await createTemplate(templateData);
      }

      setNotify({
        title: "Sucesso!",
        message: `Template ${isEditing ? 'atualizado' : 'criado'} com sucesso!`,
        type: "success",
        visible: true,
      });

      setTimeout(() => {
        handleBackNavigation();
      }, 1500);

    } catch (error) {
      console.error('Erro ao salvar template:', error);
      
      // Tratamento específico para erro da API com key 'error'
      let errorMessage = `Erro ao ${isEditing ? 'atualizar' : 'criar'} template.`;
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotify({
        title: "Erro!",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) return;

    setIsGenerating(true);
    
    try {
      // Criar prompt detalhado para gerar HTML de email profissional
      const prompt = `Crie um template HTML completo e profissional para email sobre: "${aiPrompt}"

REQUISITOS OBRIGATÓRIOS:
1. HTML completo com DOCTYPE, head e body
2. Design responsivo e moderno
3. Cores azuis (#3b82f6 ou gradient var(--gradient-primary))
4. Usar shortcodes: {{nome_cliente}}, {{telefone}}, {{data_agendamento}}
5. Estrutura: header, conteúdo principal, seção destacada, footer
6. Fonte moderna (Segoe UI, sans-serif)
7. Botão de call-to-action azul
8. Email width máximo 600px, centralizado
9. CSS inline e no head
10. Texto profissional mas amigável

ESTRUTURA DESEJADA:
- Header com gradiente azul e título
- Saudação com {{nome_cliente}}
- Conteúdo explicativo sobre "${aiPrompt}"
- Seção destacada com detalhes (data e telefone com shortcodes)
- Botão de ação
- Assinatura profissional
- Footer discreto

Retorne APENAS o código HTML completo, sem explicações.`;

      const systemMessage = `Você é um especialista em design de emails HTML profissionais. 
Crie templates responsivos, modernos e que funcionem bem em todos os clientes de email.
Use sempre as cores especificadas e mantenha o design limpo e profissional.
Retorne apenas o código HTML completo, sem markdown ou explicações adicionais.`;

      const aiResponse = await generateAIResponse({
        prompt: prompt,
        system_message: systemMessage,
        model: "gpt-4o-mini",
        max_tokens: 2000,
        temperature: 0.7
      });

      if (aiResponse.success) {
        const generatedHTML = aiResponse.response;
        
        setValue("email_html_content", generatedHTML);
        setValue("email_subject", `${aiPrompt} - {{nome_cliente}}`);
        
        setShowAIGenerator(false);
        setAiPrompt("confirmação de agendamento para consultório médico");
        
        setNotify({
          title: "IA Concluída!",
          message: "Email HTML gerado com sucesso usando IA!",
          type: "success",
          visible: true,
        });
      } else {
        // Tratamento específico de erros da IA
        let errorMessage = "Erro ao gerar email com IA. Tente novamente.";
        
        if (aiResponse.errorType === "AI_API_ERROR") {
          errorMessage = "Serviço de IA temporariamente indisponível. Tente novamente em alguns minutos.";
        } else if (aiResponse.errorType === "VALIDATION_ERROR") {
          errorMessage =
            aiResponse.error ||
            "Dados inválidos para geração. Verifique o prompt e tente novamente.";
        } else if (aiResponse.errorType === "CONNECTION_ERROR") {
          errorMessage = "Problemas de conexão. Verifique sua internet e tente novamente.";
        } else if (aiResponse.error?.includes("API key") || aiResponse.error?.includes("obrigatória")) {
          errorMessage = "Configuração de IA pendente. Entre em contato com o suporte.";
        }

        setNotify({
          title: "Erro na IA",
          message: errorMessage,
          type: "error",
          visible: true,
        });
      }

    } catch (error) {
      console.error('Erro inesperado na geração de IA:', error);
      
      // Tratamento específico para erro da API com key 'error'
      let errorMessage = "Erro inesperado ao gerar email. Tente novamente.";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotify({
        title: "Erro na IA",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail || !watchedValues.email_subject || !watchedValues.email_html_content) return;
    
    setIsTesting(true);
    
    try {
      const templateData = {
        email_display_name: watchedValues.email_display_name,
        email_subject: watchedValues.email_subject,
        email_html_content: watchedValues.email_html_content,
        test_email: testEmail
      };

      await testTemplateDraft(templateData);
      
      setTestResult({ 
        success: true, 
        message: "Email de teste enviado com sucesso!" 
      });
    } catch (error) {
      console.error('Erro no teste de email:', error);
      
      // Tratamento específico para erro da API com key 'error'
      let errorMessage = "Falha ao enviar email de teste. Verifique os dados e tente novamente.";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setTestResult({ 
        success: false, 
        message: errorMessage
      });
    } finally {
      setIsTesting(false);
    }
    
    setTimeout(() => setTestResult(null), 5000);
  };

  const insertShortcode = (shortcode) => {
    const currentContent = watchedValues.email_html_content || "";
    setValue("email_html_content", currentContent + shortcode);
  };

  if (isLoading) {
    return (
      <Container>
        <div className="d-flex justify-content-center my-5">
          <div>Carregando...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBackNavigation}>
            <IconArrowLeft size={20} />
          </BackButton>
          <div>
            <Title>
              {isEditing ? 'Editar' : 'Criar'} Template de Email
            </Title>
            <Subtitle>
              Configure seu template para envio de emails automáticos
            </Subtitle>
          </div>
        </HeaderLeft>
      </Header>

      <ContentContainer>
        <FormSection>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Label>Nome do Template</Label>
              <Input
                type="text"
                placeholder="Ex: Confirmação de Agendamento por Email"
                {...register("name", { required: "Nome é obrigatório" })}
                error={!!errors.name}
              />
              {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
            </FormGroup>

          {/*   <FormGroup>
              <Label>Email do Remetente</Label>
              <Input
                type="email"
                placeholder="noreply@suaempresa.com"
                {...register("email_from", { 
                  required: "Email do remetente é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido"
                  }
                })}
                error={!!errors.email_from}
              />
              {errors.email_from && <ErrorText>{errors.email_from.message}</ErrorText>}
              <HelpText>Este email aparecerá como remetente das mensagens</HelpText>
            </FormGroup> */}

            <FormGroup>
              <Label>Título do Email</Label>
              <Input
                type="text"
                placeholder="Ex: Mercado Agentes 🚀"
                maxLength={40}
                {...register("email_display_name", { 
                  maxLength: {
                    value: 40,
                    message: "Título deve ter no máximo 40 caracteres"
                  }
                })}
                error={!!errors.email_display_name}
              />
              {errors.email_display_name && <ErrorText>{errors.email_display_name.message}</ErrorText>}
              <HelpText>Nome que aparecerá como remetente nos clientes de email (ex: "Mercado Agentes 🚀" &lt;email@empresa.com&gt;). Máximo 40 caracteres.</HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Assunto do Email</Label>
              <Input
                type="text"
                placeholder="Confirmação de Agendamento - {{nome_cliente}}"
                {...register("email_subject", { required: "Assunto é obrigatório" })}
                error={!!errors.email_subject}
              />
              {errors.email_subject && <ErrorText>{errors.email_subject.message}</ErrorText>}
              <HelpText>Use shortcodes como {`{{nome_cliente}}`}, {`{{data_agendamento}}`}</HelpText>
            </FormGroup>

            <FormGroup>
              <LabelWithActions>
                <Label>Conteúdo HTML</Label>
                <ActionButtons>
                  <ActionButton 
                    type="button"
                    onClick={() => setShowAIGenerator(true)}
                  >
                    <IconRobot size={16} />
                    Gerar com IA
                  </ActionButton>
                  <ActionButton 
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    active={showPreview}
                  >
                    <IconEye size={16} />
                    Preview
                  </ActionButton>
                </ActionButtons>
              </LabelWithActions>
              
              <TextArea
                rows={12}
                placeholder="Digite seu HTML personalizado ou use o gerador com IA"
                {...register("email_html_content", { required: "Conteúdo HTML é obrigatório" })}
                error={!!errors.email_html_content}
                style={{ width: '100%' }}
              />
              {errors.email_html_content && <ErrorText>{errors.email_html_content.message}</ErrorText>}
            </FormGroup>

            {showPreview && watchedValues.email_html_content && (
              <PreviewContainer>
                <PreviewHeader>Preview do Email</PreviewHeader>
                <PreviewFrame
                  srcDoc={watchedValues.email_html_content}
                  title="Preview do Email"
                />
              </PreviewContainer>
            )}

            <TestSection>
              <TestHeader>
                <IconSend size={18} />
                Teste de Email
              </TestHeader>
              
              <TestContainer>
                <TestInput
                  type="email"
                  placeholder="seu-email@teste.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <TestButton 
                  type="button"
                  onClick={handleTest}
                  disabled={!testEmail || !watchedValues.email_subject || !watchedValues.email_html_content || isTesting}
                >
                  {isTesting ? "Enviando..." : "Enviar Teste"}
                </TestButton>
              </TestContainer>
              
              {testResult && (
                <TestResult success={testResult.success}>
                  {testResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
                  {testResult.message}
                </TestResult>
              )}
              
              <HelpText>
                Envie um email de teste para verificar como ficará a formatação
              </HelpText>
            </TestSection>

            <FormActions>
              <SecondaryButton type="button" onClick={handleBackNavigation}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isLoading}>
                {isLoading ? (
                  "Salvando..."
                ) : (
                  <>
                    <IconDeviceFloppy size={18} />
                    {isEditing ? 'Atualizar' : 'Criar'} Template
                  </>
                )}
              </PrimaryButton>
            </FormActions>
          </Form>
        </FormSection>

        <SidebarSection>
          <ShortcodesSection>
            <ShortcodesTitle>Shortcodes Disponíveis</ShortcodesTitle>
            <ShortcodesList>
              {availableShortcodes.map((shortcode, index) => (
                <ShortcodeItem 
                  key={index}
                  onClick={() => insertShortcode(shortcode.code)}
                >
                  <ShortcodeCode>{shortcode.code}</ShortcodeCode>
                  <ShortcodeDesc>{shortcode.description}</ShortcodeDesc>
                </ShortcodeItem>
              ))}
            </ShortcodesList>
            <HelpText style={{ marginTop: '16px' }}>
              Clique em um shortcode para inserir no conteúdo HTML
            </HelpText>
          </ShortcodesSection>
        </SidebarSection>
      </ContentContainer>

      {/* Modal de Geração com IA */}
      {showAIGenerator && (
        <AIModal>
          <AIModalContent>
            <AIModalHeader>
              <h3>Gerar Email com IA</h3>
              <CloseButton onClick={() => setShowAIGenerator(false)}>×</CloseButton>
            </AIModalHeader>
            
            <AIModalBody>
              <Label>Descreva o tipo de email que deseja criar:</Label>
              <TextArea
                rows={3}
                placeholder="Ex: confirmação de agendamento, boas-vindas, lembrete de consulta..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                style={{ width: '100%' }}
              />
            </AIModalBody>
            
            <AIModalFooter>
              <SecondaryButton onClick={() => setShowAIGenerator(false)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton 
                onClick={handleGenerateWithAI} 
                disabled={!aiPrompt.trim() || isGenerating}
              >
                {isGenerating ? (
                  "Gerando..."
                ) : (
                  <>
                    <IconRobot size={16} />
                    Gerar HTML
                  </>
                )}
              </PrimaryButton>
            </AIModalFooter>
          </AIModalContent>
        </AIModal>
      )}

      {notify.visible && (
        <Notification type={notify.type}>
          <strong>{notify.title}</strong>
          <p>{notify.message}</p>
        </Notification>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background: var(--background);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const SidebarSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
  height: fit-content;
  position: sticky;
  top: 20px;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LabelWithActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'var(--surface)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border: 2px solid ${props => props.active ? 'var(--primary)' : 'var(--border-color)'};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? 'var(--primary-hover)' : 'var(--surface-hover)'};
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  resize: vertical;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
    font-family: inherit;
  }
`;

const ErrorText = styled.span`
  color: var(--danger);
  font-size: 12px;
`;

const HelpText = styled.span`
  color: var(--text-muted);
  font-size: 12px;
`;

const PreviewContainer = styled.div`
  margin-top: 16px;
`;

const PreviewHeader = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 400px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: white;
`;

const TestSection = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 20px;
`;

const TestHeader = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TestContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TestInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TestButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TestResult = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.success ? 'var(--success-50)' : 'var(--danger-50)'};
  color: ${props => props.success ? 'var(--success)' : 'var(--danger)'};
  margin-bottom: 12px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SecondaryButton = styled.button`
  background: var(--surface);
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShortcodesSection = styled.div``;

const ShortcodesTitle = styled.h3`
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const ShortcodesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShortcodeItem = styled.div`
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--primary);
  }
`;

const ShortcodeCode = styled.div`
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: var(--primary);
  font-size: 12px;
  margin-bottom: 4px;
`;

const ShortcodeDesc = styled.div`
  color: var(--text-secondary);
  font-size: 11px;
`;

// Modal de IA
const AIModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1010;
`

const AIModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const AIModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;

  h3 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--text-primary);
  }
`;

const AIModalBody = styled.div`
  padding: 24px;
`;


const AIModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding: 0 24px 24px 24px;
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.type === 'success' ? 'var(--success)' : 'var(--danger)'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

export default EmailTemplateForm;